// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN VOCABULARY — fragments de rendu purs
// Extrait de page-admin-vocabulary.ts (découpe god-function `renderVocabularyPage`
// 2026-07) : builders de sections/lignes PURS (aucun état de closure). `canWrite`
// (immuable sur un rendu) threadé explicitement. Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t.

import type { VocabItem } from './page-admin-vocabulary.js';

const buildRowVocab = (it: VocabItem, canWrite: boolean): string => {
  const overridden = it.current != null;
  const value = overridden ? (it.current ?? '') : '';
  const placeholder = it.default;
  const restoreBtn =
    overridden && canWrite
      ? `<button class="btn btn-secondary btn-icon vocab-restore" data-key="${esc(it.key)}"
                 title="${esc(t('common.restore_default_with_value', { value: it.default }))}"
                 aria-label="${esc(t('common.restore_default'))}">↺</button>`
      : '<span style="display:inline-block;width:32px"></span>';
  const disabled = canWrite ? '' : 'disabled';
  return `<tr data-key="${esc(it.key)}"${overridden ? ' class="vocab-row-overridden"' : ''}>
      <td style="font-family:var(--font-mono);font-size:12px;color:var(--text3);padding-right:12px">
        <code>${esc(it.key)}</code>
      </td>
      <td style="color:var(--text3);font-size:12px;padding-right:12px;font-style:italic">
        ${esc(it.default)}
      </td>
      <td>
        <input type="text" class="vocab-input" data-key="${esc(it.key)}"
               value="${esc(value)}" placeholder="${esc(placeholder)}"
               maxlength="2000" ${disabled}
               style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);font-size:13px">
      </td>
      <td style="width:40px;text-align:right">${restoreBtn}</td>
    </tr>`;
};

const buildRowPhrase = (it: VocabItem, canWrite: boolean): string => {
  const overridden = it.current != null;
  const value = overridden ? (it.current ?? '') : '';
  const placeholder = it.default;
  const restoreBtn =
    overridden && canWrite
      ? `<button class="btn btn-secondary btn-icon vocab-restore" data-key="${esc(it.key)}"
                 title="${esc(t('common.restore_default'))}"
                 aria-label="${esc(t('common.restore_default'))}">↺</button>`
      : '<span style="display:inline-block;width:32px"></span>';
  const disabled = canWrite ? '' : 'disabled';
  return `<div data-key="${esc(it.key)}" class="vocab-phrase${overridden ? ' vocab-row-overridden' : ''}"
              style="border:1px solid var(--border);border-radius:var(--radius);padding:10px 12px;margin-bottom:10px;background:var(--bg2)">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px">
        <code style="font-family:var(--font-mono);font-size:11px;color:var(--text3);flex:1">${esc(it.key)}</code>
        ${restoreBtn}
      </div>
      <div style="font-size:12px;color:var(--text3);font-style:italic;margin-bottom:6px">
        Défaut : ${esc(it.default)}
      </div>
      <textarea class="vocab-input" data-key="${esc(it.key)}"
                placeholder="${esc(placeholder)}"
                maxlength="2000" ${disabled} rows="2"
                style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);font-size:13px;font-family:inherit;resize:vertical">${esc(value)}</textarea>
    </div>`;
};

/** Sections « Vocabulaire » (une carte + tableau par groupe non vide). */
export function buildVocabSectionHtml(
  vocabGroups: ReadonlyArray<{ prefix: string; label: string; items: VocabItem[] }>,
  canWrite: boolean,
): string {
  return vocabGroups
    .map(
      (g) => `
    <div class="card" style="margin-bottom:16px">
      <h3 style="margin-top:0;font-size:14px;font-weight:600;color:var(--text2);text-transform:uppercase;letter-spacing:.05em">
        ${esc(g.label)}
      </h3>
      <div class="table-wrap"><table style="width:100%">
        <thead><tr style="font-size:11px;text-transform:uppercase;color:var(--text3);letter-spacing:.05em">
          <th style="text-align:left;padding:6px 12px 6px 0">Clé</th>
          <th style="text-align:left;padding:6px 12px 6px 0">Défaut</th>
          <th style="text-align:left;padding:6px 0">Surcharge</th>
          <th></th>
        </tr></thead>
        <tbody>${g.items.map((it) => buildRowVocab(it, canWrite)).join('')}</tbody>
      </table></div>
    </div>
  `,
    )
    .join('');
}

/** Section repliable « Phrases personnalisables » (vide → chaîne vide). */
export function buildPhrasesSectionHtml(phraseItems: VocabItem[], canWrite: boolean): string {
  if (!phraseItems.length) return '';
  const phrasesOverridden = phraseItems.filter((it) => it.current != null).length;
  return `
    <details class="card" style="margin-bottom:16px;padding:12px 16px"${phrasesOverridden > 0 ? ' open' : ''}>
      <summary style="cursor:pointer;font-size:14px;font-weight:600;color:var(--text2);text-transform:uppercase;letter-spacing:.05em;list-style:none">
        Phrases personnalisables
        <span style="text-transform:none;letter-spacing:0;font-weight:400;color:var(--text3);font-size:12px;margin-left:8px">
          (${phraseItems.length} phrase${phraseItems.length > 1 ? 's' : ''}${phrasesOverridden > 0 ? ` · ${phrasesOverridden} surchargée${phrasesOverridden > 1 ? 's' : ''}` : ''}) — Vouvoiement obligatoire
        </span>
      </summary>
      <p style="font-size:12px;color:var(--text3);margin:8px 0 14px 0;font-style:italic">
        Les phrases d'instruction s'adressent à vos utilisateurs. Conservez le vouvoiement
        (« vous », « votre ») pour rester cohérent avec le ton produit Ornexia.
      </p>
      <div>${phraseItems.map((it) => buildRowPhrase(it, canWrite)).join('')}</div>
    </details>
  `;
}
